import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import useForm from '@app/hooks/use-form';
import LocalizedText from '@LocalizedText';
import { setResizeParams } from '@app/actions/customizeActions';
import { roundNum, isMobileMode, isCustomShape, checkShapeIsEquilateral } from '@app/utils/utils';
import { SIZE_ADJUST_CONFIG } from '@app/utils/shapeConfigs';
import { isCreatedFromDielineShape } from '@app/utils/hints';
import { toTwips, fromTwips } from '@app/utils/measurement';
import ChangeMeasurementTool from '../change-measurement-tool/ChangeMeasurementTool';
import ResizeInput from './ResizeInput';
import './ResizeTool.scss';
import { inject } from '@app/providers/QAU.ContextProvider';

const validateAspect = (width, height, max) =>
  (height <= max && width <= max) || (height > max && width <= max) || (width > max && height <= max);

const ResizeTool = ({
  measurementLocale: locale = 'inches',
  resizeParams,
  panelSizes: master = { width: 0, height: 0 },
  setResizeParams,
  isMobileView,
  currentFlow,
  disabled,
  clear,
  createdFromDielineShape,
}) => {
  const unitsScale = { millimeters: 2, inches: 4 };
  const {
    product = { min: [0, 0], max: [Infinity, Infinity] },
    selectedShape,
    prevSelectedShape,
    isShapeChanged,
    shapeByCornerType,
    prevShapeByCornerType,
    selectedSizes,
    shouldReset,
  } = resizeParams;
  const newShape = shapeByCornerType || selectedShape;
  const prevShape = prevShapeByCornerType || prevSelectedShape;
  const width = useMemo(() => fromTwips(master.width, locale), [master.width, locale]);
  const height = useMemo(() => fromTwips(master.height, locale), [master.height, locale]);
  const selectedWidth = useMemo(() => fromTwips(selectedSizes.width, locale), [selectedSizes.width, locale]);
  const selectedHeight = useMemo(() => fromTwips(selectedSizes.height, locale), [selectedSizes.height, locale]);
  const min = [fromTwips(product.min[0], locale), fromTwips(product.min[1], locale)];
  const max = [fromTwips(product.max[0], locale), fromTwips(product.max[1], locale)];
  const widthAspectRatio = useMemo(() => master.height / master.width, [master.height, master.width]);
  const heightAspectRatio = useMemo(() => master.width / master.height, [master.height, master.width]);
  const [isAspectRatioLocked, setAspectRatioLocked] = useState(true);
  const isCustomShapeSelected = isCustomShape(newShape);
  const isOneDimensionShapeSelected = checkShapeIsEquilateral(newShape);
  const isAspectRatioCheckboxVisible = isCustomShapeSelected || createdFromDielineShape;
  const isAspectRatioLockIconVisible = isCustomShapeSelected || isOneDimensionShapeSelected || createdFromDielineShape;
  const isAspectRatioLockIconDisabled = isOneDimensionShapeSelected && !createdFromDielineShape;

  const toggleAspectRatio = () => setAspectRatioLocked(!isAspectRatioLocked);
  const onAspectRatioChange = ({ target: { checked } }) => setAspectRatioLocked(checked);

  const onValidateSizes = ({ width, height, sideMin, sideMax, verticalMax, horizontalMax }) => {
    const errors = {};
    errors.width = !width || width < sideMin || width > sideMax;
    errors.height = !height || height < sideMin || height > sideMax;
    errors.aspect = !validateAspect(width, height, Math.min(verticalMax, horizontalMax));
    return errors;
  };

  const onInputBlur = (values, touched) => {
    const calculatedValue = touched.width ? values.heightAspectRatio * values.height : values.widthAspectRatio * values.width;
    return isAspectRatioLocked && roundNum(calculatedValue, unitsScale[locale]);
  };

  const onSubmitByEnter = () => setResizeParams({ shouldApply: true });

  const handleFormChange = useCallback(
    (next, prev, changed) => {
      let nextWidth = +next.width;
      let nextHeight = +next.height;
      if (!nextWidth || !nextHeight) return { ...next };

      if (isOneDimensionShapeSelected) {
        nextWidth = changed === 'height' ? next.height : nextWidth;
        nextHeight = changed === 'width' ? next.width : nextHeight;
      } else if (isAspectRatioLocked) {
        nextWidth = changed === 'height' ? heightAspectRatio * next.height : nextWidth;
        nextHeight = changed === 'width' ? widthAspectRatio * next.width : nextHeight;
      }

      return {
        ...next,
        width: roundNum(nextWidth, unitsScale[locale]),
        height: roundNum(nextHeight, unitsScale[locale]),
      };
    },
    [heightAspectRatio, isAspectRatioLocked, isOneDimensionShapeSelected, widthAspectRatio, locale, unitsScale],
  );

  const onValueChange = useCallback(
    (...args) => {
      return handleFormChange(...args);
    },
    [handleFormChange],
  );

  const adjustSizes = useMemo(() => {
    const shouldAdjustSize = SIZE_ADJUST_CONFIG[newShape] && SIZE_ADJUST_CONFIG[newShape].pair === prevShape && isShapeChanged;
    if (shouldAdjustSize) {
      const { adjustedWidth, adjustedHeight } = SIZE_ADJUST_CONFIG[newShape].adjust(selectedWidth, selectedHeight, max[1]);

      return {
        width: adjustedWidth || width,
        height: adjustedHeight || height,
      };
    }

    return {
      width: isOneDimensionShapeSelected ? Math.min(selectedWidth, selectedHeight) : width,
      height: isOneDimensionShapeSelected ? Math.min(selectedWidth, selectedHeight) : height,
    };
  }, [newShape, prevShape, isShapeChanged, isOneDimensionShapeSelected, selectedWidth, selectedHeight, width, height, max]);

  const { handleChange, handleBlur, handleSubmit, values, changed, touched, errors } = useForm(
    {
      width: adjustSizes.width,
      height: adjustSizes.height,
      heightAspectRatio,
      widthAspectRatio,
      sideMin: (isOneDimensionShapeSelected ? Math.max : Math.min)(...min),
      sideMax: (isOneDimensionShapeSelected ? Math.min : Math.max)(...max),
      verticalMax: Math.min(...max),
      horizontalMax: Math.max(...max),
      unitScale: unitsScale[locale],
    },
    {
      onSubmit: onSubmitByEnter,
      onValidate: onValidateSizes,
      onChange: onValueChange,
      onBlur: onInputBlur,
      isAspectRatioLocked: isAspectRatioLocked,
      resetValues: shouldReset,
    },
    [locale, height, width, shouldReset, isOneDimensionShapeSelected, newShape],
  );

  useEffect(() => {
    const isAspectChecked = isOneDimensionShapeSelected || isCustomShapeSelected || createdFromDielineShape;

    setAspectRatioLocked(isAspectChecked);
  }, [isOneDimensionShapeSelected, isCustomShapeSelected, createdFromDielineShape]);

  useEffect(() => {
    const isSizeChanged = changed.height || changed.width || errors.aspect;
    const hasValidationErrors = errors.aspect || errors.width || errors.height;

    setResizeParams({
      isSizeChanged,
      hasValidationErrors,
      shouldReset: false,
    });
  }, [changed, errors, touched]);

  useEffect(() => {
    const { selectedSizes } = resizeParams;
    const { width, height } = values;
    const twipsWidth = toTwips(width, locale);
    const twipsHeight = toTwips(height, locale);

    setResizeParams({
      prevSizes: selectedSizes,
      selectedSizes: {
        width: twipsWidth,
        height: twipsHeight,
      },
      lockStickerAspectRatio: isAspectRatioLocked,
    });
  }, [values]);

  const handleKeyPress = useCallback(
    ({ key, target }) => {
      const valuesAreValid = !(errors.height || errors.width || errors.aspect);
      const valueIsChanged = changed.height || changed.width;

      key === '.' && (target.value = `${target.value}.`);
      key === 'Enter' && valueIsChanged && valuesAreValid && handleSubmit();
    },
    [changed, errors, handleSubmit],
  );

  const showValidationMessage = useMemo(() => isMobileView && (errors.width || errors.height), [isMobileView, errors]);

  const ResizeToolContainer = isMobileView ? Fragment : "div";
  const containerProps = isMobileView ? {} : {
    "aria-disabled": disabled,
    "className": `resize-tool-group ${clear ? 'clear' : ''}`,
  };

  return (
    <ResizeToolContainer {...containerProps}>
      {!isMobileView && (
        <div className="tool-group-label">
          <LocalizedText id={`qau-${currentFlow}-size-title`} tagName="b" />
        </div>
      )}
      <ChangeMeasurementTool />
      {isMobileView && !!isAspectRatioCheckboxVisible && (
        <div className="resize-tool-ratio">
          <label htmlFor="aspect-ratio-checkbox">
            <input type="checkbox" id="aspect-ratio-checkbox" onChange={onAspectRatioChange} checked={isAspectRatioLocked} />
            <LocalizedText id="qau-sticker-size-aspect-ratio-text" tagName="span" />
          </label>
        </div>
      )}
      <div className={`resize-input-container ${isMobileView && disabled ? 'disabled' : ''}`}>
        <div className="resize-input-fields-wrapper">
          <ResizeInput
            side="height"
            values={values}
            errors={errors}
            locale={locale}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleKeyPress={handleKeyPress}
            titleKey={'qau-height-text'}
          />
          <ResizeInput
            side="width"
            values={values}
            errors={errors}
            locale={locale}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleKeyPress={handleKeyPress}
            titleKey={`qau-width-text`}
          />
        </div>
        {!isMobileView && !!isAspectRatioLockIconVisible && (
          <div aria-disabled={isAspectRatioLockIconDisabled} className={classnames('resize-lock-icon-container', { invalid: errors.width })}>
            <div className="resize-lock-icon-wrapper" onClick={toggleAspectRatio}>
              <span className={`resize-lock-icon ${isAspectRatioLocked ? 'locked' : 'unlocked'}`} />
            </div>
          </div>
        )}
      </div>
      {!isMobileView && !!isAspectRatioCheckboxVisible && (
        <div className="resize-tool-ratio">
          <label htmlFor="aspect-ratio-checkbox">
            <input type="checkbox" id="aspect-ratio-checkbox" onChange={onAspectRatioChange} checked={isAspectRatioLocked} />
            <LocalizedText id="qau-sticker-size-aspect-ratio-text" tagName="span" />
          </label>
        </div>
      )}
      <div className={classnames('resize-tool-validation-info', { invalid: errors.aspect || showValidationMessage })}>
        {showValidationMessage ? (
          <LocalizedText
            id={`qau-field-validation-${locale}${isMobileView ? '-mobile' : ''}`}
            values={{
              min: values.sideMin,
              max: values.sideMax,
            }}
          />
        ) : (
          <LocalizedText
            id={`qau-aspect-${currentFlow}-${
              isOneDimensionShapeSelected || product.max[0] === product.max[1] ? 'equilateral-' : ''
            }${locale}${isMobileView ? '-mobile' : ''}`}
            values={{
              vertical: values.verticalMax,
              horizontal: values.horizontalMax,
            }}
          />
        )}
      </div>
    </ResizeToolContainer>
  );
};

const mapStateToProps = ({
  customize: {
   customizeData: {
     measurementLocale,
     resizeParams,
     panelSizes,
   },
  },
  matrix
}, { context }) => ({
  measurementLocale,
  resizeParams,
  panelSizes,
  isMobileView: isMobileMode(matrix.ctx),
  createdFromDielineShape: isCreatedFromDielineShape(context),
});

const mapDispatchToProps = {
  setResizeParams,
};

ResizeTool.propTypes = {
  setResizeParams: PropTypes.func.isRequired,
  resizeParams: PropTypes.object.isRequired,
  panelSizes: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  clear: PropTypes.bool,
  createdFromDielineShape: PropTypes.bool.isRequired,
  context: PropTypes.object.isRequired,
};

export default inject('context')(connect(mapStateToProps, mapDispatchToProps)(ResizeTool));